import { createContext, useContext, useId } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface FieldContextValue {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Inside a <Field>: the control's id, aria-describedby and aria-invalid. */
export function useFieldControl(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  readonly label: ReactNode;
  /** Error text (12px, --danger-fg, role=alert); also marks the control aria-invalid. */
  readonly error?: ReactNode;
  /** Help text under the control (12px, fg-3). */
  readonly hint?: ReactNode;
  /** Explicit id for the control; generated otherwise. */
  readonly id?: string;
  readonly className?: string;
  /** One TextInput / Select / Textarea / DateInput (or any control reading useFieldControl). */
  readonly children: ReactNode;
}

/**
 * Labelled form field from the design's drawers and dialogs: label 12px/500 fg-2 above the
 * control (column, gap 4px), then an inline error or hint.
 */
export function Field({ label, error, hint, id, className, children }: FieldProps) {
  const generated = useId();
  const controlId = id ?? `${generated}-control`;
  const errorId = `${controlId}-error`;
  const hintId = `${controlId}-hint`;
  const hasError = error !== undefined && error !== null && error !== false && error !== '';
  const showHint = hint !== undefined && !hasError;
  const describedBy = hasError ? errorId : showHint ? hintId : undefined;

  return (
    <FieldContext.Provider value={{ id: controlId, describedBy, invalid: hasError }}>
      <div className={cn('flex flex-col gap-1', className)}>
        <label htmlFor={controlId} className="text-[12px] font-medium text-fg-2">
          {label}
        </label>
        {children}
        {hasError && (
          <span id={errorId} role="alert" className="text-[12px] text-danger-fg">
            {error}
          </span>
        )}
        {showHint && (
          <span id={hintId} className="text-[12px] text-fg-3">
            {hint}
          </span>
        )}
      </div>
    </FieldContext.Provider>
  );
}
